import React, { useMemo, useState } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, QuestionMarkCircleIcon, MinusCircleIcon } from '@heroicons/react/solid';

/**
 * HfpV4ClinicianView (TDI-526)
 * ----------------------------
 * The clinical team's view of a v4 strengths and needs profile: fixed,
 * deterministic, and carrying no AI-written text (ruling D78: the HFP sits
 * outside the registered RMS device, so clinicians see only a fixed,
 * consented, labelled view). It reads the clinician view the report record
 * serves (TDI-522, `rmsGetHfpV4Report` for a provider) and computes nothing
 * itself: every level, word, caption and line is already in the data.
 *
 * Shape: a scope line and the not-a-diagnosis notice; the six groups with
 * their domain colours (professional views only, D119); each area with its
 * Home and School level in the four chip words, the caption when a third or
 * more answers were Not sure, "Not asked" where a side was never asked, the
 * safety notes; and the evidence for the area, item by item, with the ICF
 * code and each side's answer in the scale's own words. The health checks
 * close the view (D92: professionals). Print renders every area in full.
 *
 * Nothing here is shown without the family's yes: the page gates on the
 * record's sharing flag before asking for the view, and the server refuses
 * the call without it.
 */

const SETTINGS = [
  { key: 'home', label: 'Home', who: 'parent or carer' },
  { key: 'school', label: 'School', who: 'teacher' },
];

// Every level carries colour, icon and the chip word together, so meaning
// never rides on colour alone.
const LEVEL_STYLE = {
  green: { box: 'bg-green-50 border border-green-200', icon: CheckCircleIcon, iconClass: 'text-green-700' },
  amber: { box: 'bg-amber-50 border border-amber-200', icon: ExclamationCircleIcon, iconClass: 'text-amber-700' },
  red: { box: 'bg-red-50 border border-red-200', icon: ExclamationCircleIcon, iconClass: 'text-red-700' },
  grey: { box: 'bg-gray-100 border border-gray-200', icon: QuestionMarkCircleIcon, iconClass: 'text-gray-600' },
};
const NOT_ASKED_STYLE = { box: 'bg-white border border-dashed border-gray-300', icon: MinusCircleIcon, iconClass: 'text-gray-500' };

function fixedText(view, lineId, fallback = '') {
  const line = view && view.fixed ? view.fixed[lineId] : null;
  return (line && line.text) || fallback;
}

function fill(text, child) {
  if (!text) return '';
  return String(text)
    .replace(/\[Name\]/g, (child && child.first_name) || 'the child')
    .replace(/\[School\]/g, (child && child.school_name) || 'the school');
}

/**
 * A health check's words. The model words a check once when only one side
 * raised it or both sides said the same; otherwise it is a map of setting to
 * words, shown one line per side.
 */
export function healthCheckLines(check) {
  const text = check && check.text;
  if (!text) return [];
  if (typeof text === 'string') return [{ setting: null, text }];
  return SETTINGS.filter((s) => text[s.key]).map((s) => ({ setting: s.key, text: text[s.key] }));
}

export function noticedLine(check) {
  const by = (check && check.noticed_by) || [];
  if (by.includes('home') && by.includes('school')) return 'Noticed at home and at school';
  if (by.includes('school')) return 'Noticed at school';
  if (by.includes('home')) return 'Noticed at home';
  return '';
}

function LevelChip({ view, side }) {
  const colour = side && side.colour;
  const word = side && side.chip_word ? fixedText(view, side.chip_word, side.chip_word) : '';
  const asked = side ? side.asked !== false : false;
  const style = asked && colour ? LEVEL_STYLE[colour] || LEVEL_STYLE.grey : NOT_ASKED_STYLE;
  const Icon = style.icon;
  return (
    <div className={`rounded-lg px-3 py-2 flex flex-col gap-0.5 ${style.box}`} data-colour={asked ? colour || 'none' : 'not-asked'}>
      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
        <Icon className={`h-4 w-4 flex-shrink-0 ${style.iconClass}`} aria-hidden="true" />
        {word || 'Not asked'}
      </span>
      {asked && side && side.caption && side.caption.text ? (
        <span className="text-sm text-gray-600">{side.caption.text}</span>
      ) : null}
    </div>
  );
}

// The ICF component an item belongs to, named from the first letter of its
// id, which is the ICF code (b1140, d110: the ICF's own convention). A label, not a score: the model rates
// areas, and nothing here rolls items up by component.
const ICF_COMPONENTS = {
  b: 'Body functions',
  s: 'Body structures',
  d: 'Activities and participation',
  e: 'Environmental factors',
};

export function icfComponent(code) {
  const letter = String(code || '').trim().charAt(0).toLowerCase();
  return ICF_COMPONENTS[letter] || null;
}

function answerWords(cell) {
  if (!cell) return { word: 'Not asked', score: null };
  if (cell.asked === false) return { word: 'Not asked', score: null };
  if (cell.score === null || cell.score === undefined) return { word: cell.word || 'Not sure', score: null };
  return { word: cell.word || String(cell.score), score: cell.score };
}

function EvidenceTable({ area }) {
  const rows = (area && area.evidence) || [];
  if (rows.length === 0) {
    return <p className="text-sm text-gray-600">No answers were recorded for this area.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <caption className="sr-only">Answers behind {area.reader_name}, home and school</caption>
        <thead>
          <tr className="text-left text-sm text-gray-600 border-b border-gray-200">
            <th scope="col" className="py-2 pr-3 font-semibold">Question</th>
            <th scope="col" className="py-2 pr-3 font-semibold">ICF</th>
            <th scope="col" className="py-2 pr-3 font-semibold">Home</th>
            <th scope="col" className="py-2 font-semibold">School</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const home = answerWords(row.home);
            const school = answerWords(row.school);
            return (
              <tr key={row.item} className="border-b border-gray-100 align-top">
                <th scope="row" className="py-2 pr-3 font-normal text-gray-900">
                  <span className="block">{row.label}</span>
                  {row.clinical_label && row.clinical_label !== row.label ? (
                    <span className="block text-sm text-gray-500">{row.clinical_label}</span>
                  ) : null}
                </th>
                <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">{/* the item id is the ICF code; icf is its category */}
                  <span className="font-mono text-sm">{row.item}</span>
                  {row.icf ? <span className="block text-sm text-gray-500">{row.icf}</span> : null}
                  {icfComponent(row.item) ? <span className="block text-sm text-gray-500 whitespace-normal">{icfComponent(row.item)}</span> : null}
                </td>
                <td className="py-2 pr-3 text-gray-900">
                  {home.word}
                  {home.score !== null ? <span className="ml-1 text-sm text-gray-500 tabular-nums">({home.score > 0 ? `+${home.score}` : home.score})</span> : null}
                </td>
                <td className="py-2 text-gray-900">
                  {school.word}
                  {school.score !== null ? <span className="ml-1 text-sm text-gray-500 tabular-nums">({school.score > 0 ? `+${school.score}` : school.score})</span> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AreaCard({ view, area, child }) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 p-4 mb-4 break-inside-avoid" aria-labelledby={`area-${area.unit}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h3 id={`area-${area.unit}`} className="text-base font-semibold text-gray-900">
          {area.reader_name}
          <span className="ml-2 text-sm font-normal text-gray-500">{area.kind === 'sense' ? 'Sense' : 'Area'}</span>
        </h3>
        <dl className="grid grid-cols-2 gap-2 min-w-[260px]">
          {SETTINGS.map((s) => (
            <div key={s.key}>
              <dt className="text-sm text-gray-600 mb-1">{s.label}</dt>
              <dd className="m-0"><LevelChip view={view} side={area[s.key]} /></dd>
            </div>
          ))}
        </dl>
      </div>
      {(area.safety_note || []).length > 0 ? (
        <ul className="mt-3 space-y-2" aria-label="Safety notes">
          {area.safety_note.map((lineId) => (
            <li key={lineId} className="rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-gray-900">
              {fill(fixedText(view, lineId, lineId), child)}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-3">
        <EvidenceTable area={area} />
      </div>
    </section>
  );
}

export default function HfpV4ClinicianView({ view, child, onBack }) {
  const groups = useMemo(() => (view && view.domains) || [], [view]);
  const [activeGroup, setActiveGroup] = useState(null);
  const current = activeGroup || (groups[0] && groups[0].id) || null;
  const areas = (view && view.areas) || {};
  const domainColours = (view && view.domain_colours) || {};
  const healthChecks = (view && view.health_checks) || [];
  const childName = (child && child.first_name) || 'the child';

  const printView = () => {
    if (typeof window !== 'undefined' && window.print) window.print();
  };

  if (!view || view.status !== 'ready') {
    return (
      <div className="p-8 text-center text-sm text-gray-600" role="status">
        The profile is not ready yet. It appears once both forms are in and the report has been built.
      </div>
    );
  }

  return (
    <div className="hfp-v4-clinician flex flex-col h-full min-h-0">
      <style>{`
        @media print {
          .hfp-v4-clinician .no-print { display: none !important; }
          .hfp-v4-clinician .print-all, .hfp-v4-clinician .print-all[hidden] { display: block !important; }
          .hfp-v4-clinician section { page-break-inside: avoid; }
        }
      `}</style>

      <header className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-gray-600">Strengths and needs profile, clinician view</p>
            <h2 className="text-lg font-semibold text-gray-900">{childName}</h2>
          </div>
          <div className="flex items-center gap-2 no-print">
            {onBack ? (
              <button type="button" onClick={onBack} className="min-h-[44px] px-4 py-2 rounded-lg border border-primary text-sm text-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary">
                Back to the record
              </button>
            ) : null}
            <button type="button" onClick={printView} className="min-h-[44px] px-4 py-2 rounded-lg bg-primary text-white text-sm hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary">
              Print this view
            </button>
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-700 max-w-3xl">{fill(fixedText(view, 'FX-CLIN-SCOPE'), child)}</p>
        <p className="mt-1 text-sm text-gray-600 max-w-3xl">{fill(fixedText(view, 'FX-LEGAL'), child)}</p>
      </header>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <nav className="no-print lg:w-64 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 bg-gray-50 p-3" aria-label="Groups">
          <ul className="flex lg:flex-col gap-1 overflow-x-auto">
            {groups.map((g) => {
              const active = g.id === current;
              return (
                <li key={g.id} className="flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveGroup(g.id)}
                    aria-current={active ? 'page' : undefined}
                    className={`w-full min-h-[44px] text-left px-3 py-2 rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${active ? 'bg-white border border-gray-300 font-semibold text-gray-900' : 'text-gray-700 hover:bg-white'}`}
                  >
                    <span className="block">{g.reader_name}</span>
                    <span className="block text-sm text-gray-500">
                      {SETTINGS.map((s) => {
                        const colour = (domainColours[s.key] || {})[g.id];
                        return colour ? `${s.label}: ${fixedText(view, `FX-CHIP-${colour.toUpperCase()}`, colour)}` : null;
                      }).filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <main className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-gray-50">
          {groups.map((g) => {
            const show = g.id === current;
            return (
              <div key={g.id} className="print-all" hidden={!show} data-group={g.id}>
                <h2 className="text-base font-semibold text-gray-900 mb-1">{g.reader_name}</h2>
                <p className="text-sm text-gray-600 mb-3">
                  {SETTINGS.map((s) => {
                    const colour = (domainColours[s.key] || {})[g.id];
                    return colour ? `${s.label}: ${fixedText(view, `FX-CHIP-${colour.toUpperCase()}`, colour)}` : `${s.label}: not rated`;
                  }).join(' · ')}
                </p>
                {(g.areas || []).map((unit) => (areas[unit] ? <AreaCard key={unit} view={view} area={areas[unit]} child={child} /> : null))}
              </div>
            );
          })}

          <section className="bg-white rounded-xl border border-gray-200 p-4 mt-2 print-all" aria-labelledby="health-checks">
            <h2 id="health-checks" className="text-base font-semibold text-gray-900">{fixedText(view, 'FX-HEAD-HEALTH', 'Health checks')}</h2>
            {healthChecks.length === 0 ? (
              <p className="text-sm text-gray-600 mt-1">No health check was raised by the answers.</p>
            ) : (
              <ul className="mt-2 space-y-2">
                {healthChecks.map((h) => (
                  <li key={h.flag} className="text-sm text-gray-900">
                    {healthCheckLines(h).map((line) => (
                      <span key={line.setting || 'both'} className="block">
                        {line.setting ? <span className="font-semibold">{line.setting === 'home' ? 'Home' : 'School'}: </span> : null}
                        {fill(line.text, child)}
                      </span>
                    ))}
                    <span className="block text-sm text-gray-600">{noticedLine(h)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
